import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  CreateUserQualificationDto,
  UpdateUserQualificationDto,
  UserQualificationsGateway,
} from '@wilson/qualification-v2/common';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { UserQualificationsV2AttachmentGatewayService } from '../../../gateways/user-qualifications-v2-attachment-gateway.service';
import { TrainingQualificationsFinalizeAttachmentsSectionComponent } from './training-qualifications-finalize-attachments-section/training-qualifications-finalize-attachments-section.component';
import {
  ExistingQualificationFromTrainings,
  NewQualificationFromTrainings,
  TrainingQualificationsFinalizeDrawerService,
} from './training-qualifications-finalize-drawer.service';
import { TrainingQualificationsFinalizeExtendExistingQualificationsSectionComponent } from './training-qualifications-finalize-extend-existing-qualifications-section/training-qualifications-finalize-extend-existing-qualifications-section.component';
import { TrainingQualificationsFinalizeFooterSectionComponent } from './training-qualifications-finalize-footer-section/training-qualifications-finalize-footer-section.component';
import { TrainingQualificationsFinalizeNewQualificationsSectionComponent } from './training-qualifications-finalize-new-qualifications-section/training-qualifications-finalize-new-qualifications-section.component';
import { TrainingQualificationsFinalizeNotesSectionComponent } from './training-qualifications-finalize-notes-section/training-qualifications-finalize-notes-section.component';
import { TrainingQualificationsFinalizeUserSectionComponent } from './training-qualifications-finalize-user-section/training-qualifications-finalize-user-section.component';

@Component({
  selector: 'wilson-training-qualifications-finalize-drawer',
  imports: [
    CommonModule,
    FormsModule,
    NzDrawerModule,
    NzFormModule,
    ReactiveFormsModule,
    TrainingQualificationsFinalizeAttachmentsSectionComponent,
    TrainingQualificationsFinalizeExtendExistingQualificationsSectionComponent,
    TrainingQualificationsFinalizeFooterSectionComponent,
    TrainingQualificationsFinalizeNewQualificationsSectionComponent,
    TrainingQualificationsFinalizeNotesSectionComponent,
    TrainingQualificationsFinalizeUserSectionComponent,
    TranslatePipe,
  ],
  templateUrl: './training-qualifications-finalize-drawer.component.html',
  styleUrl: './training-qualifications-finalize-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingQualificationsFinalizeDrawerComponent {
  private readonly trainingQualificationsFinalizeDrawerService = inject(
    TrainingQualificationsFinalizeDrawerService,
  );
  private readonly userQualificationsGateway = inject(
    UserQualificationsGateway,
  );
  private readonly userQualificationsV2AttachmentGatewayService = inject(
    UserQualificationsV2AttachmentGatewayService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);

  private _savingDrawer = new BehaviorSubject<boolean>(false);

  protected savingDrawer$ = this._savingDrawer.asObservable();
  protected drawerVisible$ =
    this.trainingQualificationsFinalizeDrawerService.finalizeDrawerVisible$;
  protected trainingsToFinalize$ =
    this.trainingQualificationsFinalizeDrawerService.trainingsToFinalize$;
  protected newQualificationFromTrainings$ =
    this.trainingQualificationsFinalizeDrawerService
      .newQualificationFromTrainings$;
  protected existingQualificationsFromTrainings$ =
    this.trainingQualificationsFinalizeDrawerService
      .existingQualificationsFromTrainings$;
  protected qualificationsToBeCreatedOrExtendedCount$ =
    this.trainingQualificationsFinalizeDrawerService
      .totalQualificationsToCreateOrExtend$;

  protected newQualificationsAcquiredAtFormControl =
    new FormControl<Date | null>(null, [Validators.required]);
  protected newQualificationsValidFromFromControl =
    new FormControl<Date | null>(null, [Validators.required]);
  protected newQualificationsValidUntilFormControl =
    new FormControl<Date | null>(null, [Validators.required]);
  protected newQualificationsInfiniteValidFormControl =
    new FormControl<boolean>(false);
  protected newQualificationsConfirmedByEmployeeFormControl =
    new FormControl<boolean>(false);

  protected existingQualificationsValidUntilFormControl =
    new FormControl<Date | null>(null, [Validators.required]);
  protected existingQualificationsInfiniteValidFormControl =
    new FormControl<boolean>(false);
  protected existingQualificationsConfirmedByEmployeeFormControl =
    new FormControl<boolean>(false);

  protected attachmentsList: NzUploadFile[] = [];
  protected notesFormControl = new FormControl<string>('');

  protected closeFinalizeDrawer(): void {
    this.trainingQualificationsFinalizeDrawerService.setFinalizeDrawerVisible(
      false,
    );
  }

  protected cancelDrawer(): void {
    this.resetDrawer();
    this.closeFinalizeDrawer();
  }

  protected saveDrawer(
    qualificationsToBeCreated: NewQualificationFromTrainings[] | null,
    qualificationsToBeExtended: ExistingQualificationFromTrainings[] | null,
  ): void {
    if (
      !qualificationsToBeCreated?.length &&
      !qualificationsToBeExtended?.length
    ) {
      return;
    }

    this._savingDrawer.next(true);

    const observables: Observable<boolean | undefined>[] = [];

    if (qualificationsToBeCreated?.length) {
      observables.push(this.createNewQualifications(qualificationsToBeCreated));
    }

    if (qualificationsToBeExtended?.length) {
      observables.push(
        this.extendExistingQualifications(qualificationsToBeExtended),
      );
    }

    if (!observables.length) {
      this._savingDrawer.next(false);
      return;
    }

    forkJoin(observables)
      .pipe(
        take(1),
        tap((results) => {
          if (results.every((result) => result === true)) {
            this.handleSuccessfulDrawerSave();
          }
        }),
        catchError(() => {
          this._savingDrawer.next(false);
          return of(undefined);
        }),
      )
      .subscribe();
  }

  private createNewQualifications(
    qualificationsToBeCreated: NewQualificationFromTrainings[] | null,
  ): Observable<boolean | undefined> {
    if (!qualificationsToBeCreated?.length) {
      return of(true);
    }

    const bulkDto: CreateUserQualificationDto[] = [];
    qualificationsToBeCreated.forEach((toBeCreated) => {
      bulkDto.push({
        userId: toBeCreated.userId,
        qualificationId: toBeCreated.qualificationId,
        acquiredAt:
          this.newQualificationsAcquiredAtFormControl.value?.toISOString() ||
          new Date().toISOString(),
        validFrom:
          this.newQualificationsValidFromFromControl.value?.toISOString() ||
          new Date().toISOString(),
        validTill:
          this.newQualificationsValidUntilFormControl.value?.toISOString() ||
          new Date().toISOString(),
        confirmedByUser:
          this.newQualificationsConfirmedByEmployeeFormControl.value || false,
        notes: this.notesFormControl.value,
        qualificationTrainingId: toBeCreated.qualificationTrainingId,
      });
    });

    return this.userQualificationsGateway
      .createUserQualifications(bulkDto)
      .pipe(
        take(1),
        switchMap((createdUserQualifications) => {
          if (
            createdUserQualifications?.length &&
            this.attachmentsList.length
          ) {
            return this.userQualificationsV2AttachmentGatewayService
              .addAttachments(
                this.attachmentsList,
                createdUserQualifications.map((q) => q.id),
              )
              .pipe(
                take(1),
                map(() => true),
                catchError(() => {
                  this.handleFailedDrawerSave();
                  return of(undefined);
                }),
              );
          }
          return of(true);
        }),
        catchError((error) => {
          this.handleFailedDrawerSave(error);
          return of(undefined);
        }),
      );
  }

  private extendExistingQualifications(
    qualificationsToBeExtended: ExistingQualificationFromTrainings[] | null,
  ): Observable<boolean | undefined> {
    if (!qualificationsToBeExtended?.length) {
      return of(true);
    }

    const bulkDto: UpdateUserQualificationDto[] = [];
    const requests: Observable<any>[] = [];
    qualificationsToBeExtended.forEach((toBeExtended) => {
      const payload: UpdateUserQualificationDto = {
        id: toBeExtended.userQualificationId,
        validTill:
          this.existingQualificationsValidUntilFormControl.value?.toISOString(),
        confirmedByUser:
          this.existingQualificationsConfirmedByEmployeeFormControl.value ||
          false,
        qualificationTrainingId: toBeExtended.qualificationTrainingId,
      };

      if (this.notesFormControl.dirty && this.notesFormControl.value !== null) {
        payload.notes = this.notesFormControl.value;
      }

      bulkDto.push(payload);
    });

    if (bulkDto.length) {
      requests.push(
        this.userQualificationsGateway.updateUserQualifications(bulkDto),
      );
    }

    if (this.attachmentsList.length) {
      requests.push(
        this.userQualificationsV2AttachmentGatewayService.addAttachments(
          this.attachmentsList,
          qualificationsToBeExtended.map((q) => q.userQualificationId),
        ),
      );
    }

    if (!requests.length) {
      return of(true);
    }

    return forkJoin(requests).pipe(
      take(1),
      map(() => true),
      catchError(() => {
        this.handleFailedDrawerSave();
        return of(undefined);
      }),
    );
  }

  private displayConflictError(): void {
    this.nzMessageService.error(
      this.translateService.instant(
        'user_qualification.create.error.already_exists',
      ),
    );
  }

  private displayGenericError(): void {
    this.nzMessageService.error(this.translateService.instant('general.error'));
  }

  private displaySuccessMessage(): void {
    this.nzMessageService.success(
      this.translateService.instant('general.saved_successfully'),
    );
  }

  private handleFailedDrawerSave(error?: any): Observable<undefined> {
    if (error && error?.error?.statusCode === 409) {
      this.displayConflictError();
    } else {
      this.displayGenericError();
    }

    timer(500)
      .pipe(take(1))
      .subscribe(() => {
        this._savingDrawer.next(false);
      });
    return of(undefined);
  }

  private handleSuccessfulDrawerSave(): void {
    this.trainingQualificationsFinalizeDrawerService.triggerSuccessfullyFinalizedTrainings();
    this.displaySuccessMessage();
    this.resetDrawer();
    this.closeFinalizeDrawer();
    this._savingDrawer.next(false);
  }

  private resetDrawer(): void {
    this.newQualificationsAcquiredAtFormControl.reset(null);
    this.newQualificationsValidFromFromControl.reset(null);
    this.newQualificationsValidUntilFormControl.reset(null);
    this.newQualificationsInfiniteValidFormControl.reset(false);
    this.newQualificationsConfirmedByEmployeeFormControl.reset(false);

    this.existingQualificationsValidUntilFormControl.reset(null);
    this.existingQualificationsInfiniteValidFormControl.reset(false);
    this.existingQualificationsConfirmedByEmployeeFormControl.reset(false);

    this.attachmentsList = [];
    this.notesFormControl.reset('');
  }

  protected isSaveButtonDisabled(
    qualificationsToBeCreatedOrExtendedCount: number | null,
    newQualificationFromTrainings: NewQualificationFromTrainings[] | null,
    existingQualificationsFromTrainings:
      | ExistingQualificationFromTrainings[]
      | null,
  ): boolean {
    if (!qualificationsToBeCreatedOrExtendedCount) {
      return true;
    }

    const hasNewQualifications =
      newQualificationFromTrainings && newQualificationFromTrainings.length;
    if (hasNewQualifications) {
      const newQualificationsValid =
        this.newQualificationsAcquiredAtFormControl.value &&
        this.newQualificationsValidFromFromControl.value &&
        this.newQualificationsValidUntilFormControl.value;

      if (!newQualificationsValid) {
        return true;
      }
    }

    const hasExistingQualifications =
      existingQualificationsFromTrainings &&
      existingQualificationsFromTrainings.length;
    if (
      hasExistingQualifications &&
      !this.existingQualificationsValidUntilFormControl.value
    ) {
      return true;
    }

    return false;
  }
}
